import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import BopSparkline, { PuntoSparkline } from './BopSparkline';
import {
  formatMB, formatIntero, formatDurata, formatDataOra,
  formatKernel, formatGiornoUTC, variazionePercentuale, formatVariazione,
} from '../utils/formatSistema';

/**
 * Storico di backup e prove di ripristino.
 *
 * Il box in dashboard risponde a "l'ultimo giro e' andato bene?". Questa
 * modale risponde alla domanda che quel box non puo' porsi, perche' guarda un
 * record solo: "sta cambiando qualcosa?". Un dump che si dimezza e un
 * ripristino che raddoppia i tempi sono entrambi esiti OK.
 *
 * Componente di sola presentazione: la chiamata HTTP resta in DashboardPage,
 * dove vivono ApiInspector e i commenti @api.
 */

export interface EventoBackup {
  id: number;
  esito: 'OK' | 'ERRORE';
  eseguitoIl: string;
  durataSec: number | null;
  dimensione: number | null;
  numeroFile: number | null;
  messaggio: string | null;
}

export interface EventoRipristino {
  id: number;
  esito: 'OK' | 'ERRORE';
  eseguitoIl: string;
  durataSec: number | null;
  dumpNome: string | null;
  dumpEseguitoIl: string | null;
  tabelleRipristinate: number | null;
  tabelleProduzione: number | null;
  tabelleMancanti: number | null;
  tabelleAllineate: number | null;
  righeRipristinate: number | null;
  righeProduzione: number | null;
  schemaPiuRecente: boolean;
  messaggio: string | null;
}

/** Stato del sistema operativo della macchina di backup, ultimo rilevamento. */
export interface HostInfo {
  host: string | null;
  salute: 'OK' | 'ERRORE' | 'SCADUTO';
  rilevatoIl: string | null;
  distro: string | null;
  kernelInEsecuzione: string | null;
  kernelPiuRecente: string | null;
  kernelDisallineato: boolean;
  pacchettiPendenti: number | null;
  pacchettiSicurezzaPendenti: number | null;
  riavvioRichiesto: boolean;
  riavvioRichiestoDa: string | null;
  ultimoAvvio: string | null;
  aggiornamentiAutomatici: boolean | null;
  riavvioAutomatico: boolean | null;
  orarioRiavvio: string | null;
  includeNonSicurezza: boolean | null;
  prossimaCorsa: string | null;
  giorniSogliaRiavvio: number;
  messaggio: string | null;
}

/** Una corsa di aggiornamento: un giorno in cui dpkg ha toccato pacchetti. */
export interface CorsaAggiornamento {
  id: number;
  host: string;
  giorno: string;
  numeroPacchetti: number;
  kernelAggiornato: boolean;
  kernelVersione: string | null;
  /** null = non si sa (wtmp non copre quel periodo), non "no". */
  riavviatoDopo: boolean | null;
  ultimoIntervento: string | null;
  pacchetti: { nome: string; da: string | null; a: string }[];
}

export interface StoricoSistema {
  giorni: number;
  database: EventoBackup[];
  documenti: EventoBackup[];
  ripristino: EventoRipristino[];
  aggiornamenti: CorsaAggiornamento[];
}

interface Props {
  storico: StoricoSistema | null;
  /** Stato corrente del SO, da GET /stato: la modale non lo richiede da sé. */
  host: HostInfo | null;
  caricamento: boolean;
  errore: string | null;
  onClose: () => void;
}

type Tab = 'database' | 'documenti' | 'ripristino' | 'aggiornamenti';

// Un dump o un archivio documenti che perde piu' di questa quota da un giro al
// successivo non e' necessariamente un guasto — si possono aver cancellato dei
// dati per davvero — ma e' l'unica cosa in questa pagina che vale la pena
// leggere subito, e nessun esito "OK" la segnalerebbe.
const SOGLIA_CALO_PCT = 10;

// La durata del ripristino e' il vero RTO. Va guardata quando cresce, non
// quando e' alta: "12 secondi" non dice niente, "12 secondi contro i 4 di
// prima" dice che il database e' triplicato.
const SOGLIA_RALLENTAMENTO = 2;

export default function StoricoSistemaModal({ storico, host, caricamento, errore, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('database');

  return (
    <Modal title="Storico sistema" onClose={onClose} size="xl">
      {caricamento && <div className="bop-empty-state">Caricamento dello storico…</div>}

      {errore && !caricamento && (
        <div className="storico-sistema__errore">{errore}</div>
      )}

      {storico && !caricamento && !errore && (
        <div className="storico-sistema">
          <p className="storico-sistema__intro">
            Ultimi {storico.giorni} giorni. Il pannello in dashboard dice se l'ultimo giro è
            andato bene; qui si vede se qualcosa sta cambiando — un dump che si rimpicciolisce
            o un ripristino che rallenta sono esiti «OK» con un problema dentro.
          </p>

          <div className="bop-tabs">
            <button
              className={`bop-tab ${tab === 'database' ? 'active' : ''}`}
              onClick={() => setTab('database')}
            >
              Backup database ({storico.database.length})
            </button>
            <button
              className={`bop-tab ${tab === 'documenti' ? 'active' : ''}`}
              onClick={() => setTab('documenti')}
            >
              Backup documenti ({storico.documenti.length})
            </button>
            <button
              className={`bop-tab ${tab === 'ripristino' ? 'active' : ''}`}
              onClick={() => setTab('ripristino')}
            >
              Prove di ripristino ({storico.ripristino.length})
            </button>
            <button
              className={`bop-tab ${tab === 'aggiornamenti' ? 'active' : ''}`}
              onClick={() => setTab('aggiornamenti')}
            >
              Sistema operativo ({storico.aggiornamenti.length})
            </button>
          </div>

          {tab === 'database' && (
            <SezioneBackup
              eventi={storico.database}
              titolo="Dimensione del dump"
              valore={(e) => e.dimensione}
              formato={(v) => formatMB(v) ?? '—'}
              etichettaColonna="Dimensione"
              nomeMisura="dimensione del dump"
            />
          )}

          {tab === 'documenti' && (
            <SezioneBackup
              eventi={storico.documenti}
              titolo="File sincronizzati"
              valore={(e) => e.numeroFile}
              formato={(v) => `${formatIntero(v) ?? '—'} file`}
              etichettaColonna="File"
              nomeMisura="numero di file"
            />
          )}

          {tab === 'ripristino' && <SezioneRipristino eventi={storico.ripristino} />}

          {tab === 'aggiornamenti' && (
            <SezioneAggiornamenti corse={storico.aggiornamenti} host={host} />
          )}
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Backup: database (dimensione del dump) e documenti (numero di file)
// ─────────────────────────────────────────────────────────────────────────────

interface PropsBackup {
  eventi: EventoBackup[];
  titolo: string;
  valore: (e: EventoBackup) => number | null;
  formato: (v: number) => string;
  etichettaColonna: string;
  nomeMisura: string;
}

function SezioneBackup({ eventi, titolo, valore, formato, etichettaColonna, nomeMisura }: PropsBackup) {
  // Gli eventi arrivano dal piu' recente: il grafico li vuole in ordine
  // cronologico, la tabella li tiene come sono (si legge dall'ultimo giro).
  const cronologici = useMemo(() => [...eventi].reverse(), [eventi]);

  const punti: PuntoSparkline[] = cronologici.map(e => {
    const v = valore(e);
    return {
      valore: v,
      etichetta: `${formatDataOra(e.eseguitoIl)} · ${v !== null ? formato(v) : 'fallito'}`,
      errore: e.esito === 'ERRORE',
    };
  });

  const falliti = eventi.filter(e => e.esito === 'ERRORE').length;
  const ultimo = eventi[0] ? valore(eventi[0]) : null;
  const precedente = eventi[1] ? valore(eventi[1]) : null;
  const variazione = variazionePercentuale(ultimo, precedente);
  const calo = variazione !== null && variazione <= -SOGLIA_CALO_PCT;

  if (eventi.length === 0) {
    return <div className="bop-empty-state">Nessun backup registrato nel periodo</div>;
  }

  return (
    <div className="storico-sistema__sezione">
      <div className="storico-sistema__grafico">
        <div className="storico-sistema__grafico-titolo">{titolo}</div>
        <BopSparkline
          punti={punti}
          formato={formato}
          descrizione={`Andamento della ${nomeMisura} negli ultimi ${eventi.length} backup`}
        />
      </div>

      <div className="storico-sistema__riepilogo">
        <Dato etichetta="Ultimo" valore={ultimo !== null ? formato(ultimo) : 'non disponibile'} />
        <Dato
          etichetta="Rispetto al giro precedente"
          valore={formatVariazione(variazione) ?? '—'}
          tono={calo ? 'errore' : undefined}
        />
        <Dato etichetta="Giri registrati" valore={formatIntero(eventi.length) ?? '—'} />
        <Dato
          etichetta="Giri falliti"
          valore={formatIntero(falliti) ?? '—'}
          tono={falliti > 0 ? 'errore' : undefined}
        />
      </div>

      {calo && (
        <div className="storico-sistema__nota storico-sistema__nota--errore">
          La {nomeMisura} è calata del {formatVariazione(variazione)?.replace('−', '')} rispetto al
          giro precedente. Sopra il {SOGLIA_CALO_PCT}% vale la pena capire perché: se non
          corrisponde a una cancellazione voluta, il backup sta salvando meno di quello che c'è.
        </div>
      )}

      <table className="bop-table">
        <thead>
          <tr>
            <th>Data e ora</th>
            <th>Esito</th>
            <th className="bop-text-right">{etichettaColonna}</th>
            <th className="bop-text-right">Variazione</th>
            <th className="bop-text-right">Durata</th>
          </tr>
        </thead>
        <tbody>
          {eventi.map((e, i) => {
            const v = valore(e);
            const prec = eventi[i + 1] ? valore(eventi[i + 1]) : null;
            const varRiga = variazionePercentuale(v, prec);
            return (
              <tr key={e.id}>
                <td>{formatDataOra(e.eseguitoIl)}</td>
                <td><BadgeEsito esito={e.esito} messaggio={e.messaggio} /></td>
                <td className="bop-text-right">{v !== null ? formato(v) : '—'}</td>
                <td className={`bop-text-right ${varRiga !== null && varRiga <= -SOGLIA_CALO_PCT ? 'bop-text-danger' : 'bop-text-muted'}`}>
                  {formatVariazione(varRiga) ?? '—'}
                </td>
                <td className="bop-text-right">{formatDurata(e.durataSec) ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Prove di ripristino: la durata e' il dato che conta (e' il tempo di
// ripartenza reale), i conteggi dicono quanto e' completo il ripristino.
// ─────────────────────────────────────────────────────────────────────────────

function SezioneRipristino({ eventi }: { eventi: EventoRipristino[] }) {
  const cronologici = useMemo(() => [...eventi].reverse(), [eventi]);

  const punti: PuntoSparkline[] = cronologici.map(e => ({
    valore: e.durataSec,
    etichetta: `${formatDataOra(e.eseguitoIl)} · ${
      e.durataSec !== null ? formatDurata(e.durataSec) : 'fallita'
    }${e.tabelleRipristinate !== null ? ` · ${formatIntero(e.tabelleRipristinate)} tabelle` : ''}`,
    errore: e.esito === 'ERRORE',
  }));

  const durate = eventi.map(e => e.durataSec).filter((d): d is number => d !== null && Number.isFinite(d));
  const minima = durate.length ? Math.min(...durate) : null;
  const ultima = eventi[0]?.durataSec ?? null;
  const falliti = eventi.filter(e => e.esito === 'ERRORE').length;

  // Rallentamento misurato contro la prova piu' veloce del periodo, non contro
  // quella precedente: la durata di un singolo giro oscilla con il carico della
  // macchina, mentre il minimo e' una base stabile.
  const rallentata = ultima !== null && minima !== null && durate.length > 2 && ultima >= minima * SOGLIA_RALLENTAMENTO;

  if (eventi.length === 0) {
    return <div className="bop-empty-state">Nessuna prova di ripristino registrata nel periodo</div>;
  }

  return (
    <div className="storico-sistema__sezione">
      <div className="storico-sistema__grafico">
        <div className="storico-sistema__grafico-titolo">Durata del ripristino</div>
        <BopSparkline
          punti={punti}
          formato={(v) => formatDurata(v) ?? '—'}
          descrizione={`Andamento della durata del ripristino nelle ultime ${eventi.length} prove`}
        />
      </div>

      <div className="storico-sistema__riepilogo">
        <Dato etichetta="Ultima prova" valore={formatDurata(ultima) ?? 'non disponibile'} />
        <Dato etichetta="Più veloce" valore={formatDurata(minima) ?? '—'} />
        <Dato etichetta="Prove registrate" valore={formatIntero(eventi.length) ?? '—'} />
        <Dato
          etichetta="Prove fallite"
          valore={formatIntero(falliti) ?? '—'}
          tono={falliti > 0 ? 'errore' : undefined}
        />
      </div>

      {rallentata && (
        <div className="storico-sistema__nota storico-sistema__nota--attenzione">
          L'ultima prova ha richiesto almeno il doppio del tempo della più veloce del periodo.
          Non è un guasto: è il tempo di ripartenza che cresce col database. Vale la pena
          rimisurarlo con calma e aggiornare l'RTO atteso.
        </div>
      )}

      <table className="bop-table">
        <thead>
          <tr>
            <th>Data e ora</th>
            <th>Esito</th>
            <th className="bop-text-right">Durata</th>
            <th className="bop-text-right">Tabelle</th>
            <th className="bop-text-right">Allineate</th>
            <th className="bop-text-right">Righe</th>
            <th>Dump usato</th>
          </tr>
        </thead>
        <tbody>
          {eventi.map(e => {
            // Tabelle mancanti perche' create da una migrazione successiva al
            // dump: fisiologico, non un buco nel backup. Vedi il commento nel
            // model RestoreReport.
            const mancanti = e.tabelleMancanti ?? 0;
            const mancantiFisiologiche = mancanti > 0 && e.schemaPiuRecente;
            return (
              <tr key={e.id}>
                <td>{formatDataOra(e.eseguitoIl)}</td>
                <td><BadgeEsito esito={e.esito} messaggio={e.messaggio} /></td>
                <td className="bop-text-right">{formatDurata(e.durataSec) ?? '—'}</td>
                <td className="bop-text-right">
                  {formatIntero(e.tabelleRipristinate) ?? '—'}
                  {e.tabelleProduzione !== null && (
                    <span className="bop-text-muted"> / {formatIntero(e.tabelleProduzione)}</span>
                  )}
                  {mancanti > 0 && (
                    <span className={mancantiFisiologiche ? 'storico-sistema__mancanti--info' : 'storico-sistema__mancanti--errore'}>
                      {' '}−{formatIntero(mancanti)}
                    </span>
                  )}
                </td>
                <td className="bop-text-right">{formatIntero(e.tabelleAllineate) ?? '—'}</td>
                <td className="bop-text-right">{formatIntero(e.righeRipristinate) ?? '—'}</td>
                <td className="storico-sistema__dump" title={e.dumpNome ?? ''}>
                  {e.dumpNome ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="storico-sistema__legenda">
        «Tabelle» confronta il ripristino con la produzione di adesso: una differenza in meno
        segnata in blu sono tabelle create da una migrazione successiva al dump e rientrano
        col backup dopo; in rosso sono tabelle che dal dump non si ricostruiscono.
        «Allineate» sono quelle con lo stesso numero di righe: il dump è la fotografia di un
        istante mentre la produzione continua a cambiare, quindi non arriverà mai al totale.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sistema operativo della macchina di backup: stato attuale e corse
// ─────────────────────────────────────────────────────────────────────────────

function SezioneAggiornamenti({ corse, host }: { corse: CorsaAggiornamento[]; host: HostInfo | null }) {
  // Una sola corsa aperta per volta: l'elenco dei pacchetti di una corsa grossa
  // è lungo centinaia di righe e due aperte insieme rendono la tabella illeggibile.
  const [aperta, setAperta] = useState<number | null>(null);

  const cronologiche = useMemo(() => [...corse].reverse(), [corse]);

  const punti: PuntoSparkline[] = cronologiche.map(c => ({
    valore: c.numeroPacchetti,
    etichetta: `${formatGiornoUTC(c.giorno)} · ${formatIntero(c.numeroPacchetti)} pacchetti${
      c.kernelAggiornato ? ` · kernel ${formatKernel(c.kernelVersione)}` : ''
    }`,
  }));

  const totale = corse.reduce((t, c) => t + c.numeroPacchetti, 0);
  const conKernel = corse.filter(c => c.kernelAggiornato).length;
  // Solo i false espliciti: il null significa "non si sa", e contarlo come
  // riavvio mancante inventerebbe un problema.
  const senzaRiavvio = corse.filter(c => c.kernelAggiornato && c.riavviatoDopo === false).length;

  return (
    <div className="storico-sistema__sezione">
      {host && host.rilevatoIl && (
        <div className="storico-sistema__grafico">
          <div className="storico-sistema__grafico-titolo">
            Stato attuale{host.host ? ` — ${host.host}` : ''}
          </div>
          <div className="storico-sistema__riepilogo">
            <Dato etichetta="Sistema operativo" valore={host.distro ?? '—'} />
            <Dato
              etichetta="Kernel in servizio"
              valore={formatKernel(host.kernelInEsecuzione) ?? '—'}
              tono={host.kernelDisallineato ? 'errore' : undefined}
            />
            <Dato
              etichetta="Kernel più recente installato"
              valore={formatKernel(host.kernelPiuRecente) ?? '—'}
              tono={host.kernelDisallineato ? 'errore' : undefined}
            />
            <Dato
              etichetta="Aggiornamenti in attesa"
              valore={
                host.pacchettiPendenti === null
                  ? '—'
                  : `${formatIntero(host.pacchettiPendenti)}${
                      host.pacchettiSicurezzaPendenti
                        ? ` (${formatIntero(host.pacchettiSicurezzaPendenti)} di sicurezza)`
                        : ''
                    }`
              }
              tono={host.pacchettiSicurezzaPendenti ? 'errore' : undefined}
            />
            <Dato etichetta="Ultimo avvio" valore={formatDataOra(host.ultimoAvvio)} />
            <Dato etichetta="Prossimo controllo" valore={formatDataOra(host.prossimaCorsa)} />
            <Dato
              etichetta="Aggiornamenti automatici"
              valore={descriviInterruttore(host.aggiornamentiAutomatici)}
              tono={host.aggiornamentiAutomatici === false ? 'errore' : undefined}
            />
            <Dato
              etichetta="Riavvio automatico"
              valore={
                host.riavvioAutomatico === true && host.orarioRiavvio
                  ? `attivo, alle ${host.orarioRiavvio}`
                  : descriviInterruttore(host.riavvioAutomatico)
              }
              tono={host.riavvioAutomatico === false ? 'errore' : undefined}
            />
          </div>

          {host.includeNonSicurezza === false && (
            <div className="storico-sistema__nota storico-sistema__nota--attenzione">
              Vengono installati solo gli aggiornamenti di sicurezza. Tutto il resto resta
              indietro senza che nulla lo segnali: è così che questa macchina è arrivata a
              153 pacchetti arretrati e un kernel di 14 mesi.
            </div>
          )}
          <div className="storico-sistema__rilevato">
            Rilevato il {formatDataOra(host.rilevatoIl)}
          </div>
        </div>
      )}

      {corse.length === 0 ? (
        <div className="bop-empty-state">Nessuna corsa di aggiornamento nel periodo</div>
      ) : (
        <>
          <div className="storico-sistema__grafico">
            <div className="storico-sistema__grafico-titolo">Pacchetti aggiornati per giorno</div>
            <BopSparkline
              punti={punti}
              formato={(v) => `${formatIntero(v)} pacchetti`}
              descrizione={`Pacchetti aggiornati in ciascuna delle ultime ${corse.length} corse`}
            />
          </div>

          <div className="storico-sistema__riepilogo">
            <Dato etichetta="Corse registrate" valore={formatIntero(corse.length) ?? '—'} />
            <Dato etichetta="Pacchetti in totale" valore={formatIntero(totale) ?? '—'} />
            <Dato etichetta="Corse col kernel" valore={formatIntero(conKernel) ?? '—'} />
            <Dato
              etichetta="Kernel senza riavvio"
              valore={formatIntero(senzaRiavvio) ?? '—'}
              tono={senzaRiavvio > 0 ? 'errore' : undefined}
            />
          </div>

          <table className="bop-table">
            <thead>
              <tr>
                <th>Giorno</th>
                <th className="bop-text-right">Pacchetti</th>
                <th>Kernel</th>
                <th>Riavvio successivo</th>
                <th className="bop-text-right">Ultimo intervento</th>
              </tr>
            </thead>
            <tbody>
              {corse.map(c => (
                <React.Fragment key={c.id}>
                  <tr
                    className="storico-sistema__corsa"
                    onClick={() => setAperta(aperta === c.id ? null : c.id)}
                  >
                    <td>
                      <span className="storico-sistema__freccia">{aperta === c.id ? '▾' : '▸'}</span>
                      {formatGiornoUTC(c.giorno)}
                    </td>
                    <td className="bop-text-right">{formatIntero(c.numeroPacchetti)}</td>
                    <td>
                      {c.kernelAggiornato
                        ? <span className="bop-badge bop-badge-info">{formatKernel(c.kernelVersione)}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td><RiavvioDopo valore={c.riavviatoDopo} kernel={c.kernelAggiornato} /></td>
                    <td className="bop-text-right">{formatDataOra(c.ultimoIntervento)}</td>
                  </tr>
                  {aperta === c.id && (
                    <tr className="storico-sistema__pacchetti-riga">
                      <td colSpan={5}>
                        {c.pacchetti.length === 0 ? (
                          <div className="bop-text-muted">Elenco dei pacchetti non disponibile</div>
                        ) : (
                          <ul className="storico-sistema__pacchetti">
                            {c.pacchetti.map(p => (
                              <li key={p.nome} className="storico-sistema__pacchetto">
                                <span className="storico-sistema__pacchetto-nome">{p.nome}</span>
                                <span className="storico-sistema__pacchetto-ver">
                                  {p.da ? `${p.da} → ${p.a}` : `installato ${p.a}`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <p className="storico-sistema__legenda">
            Una corsa è un giorno in cui sono stati installati o aggiornati pacchetti, quindi
            comprende anche gli interventi fatti a mano. «Riavvio successivo» dice se dopo quella
            corsa la macchina è stata riavviata almeno una volta: conta perché un kernel
            aggiornato non entra in servizio finché non si riavvia. Può valere «non si sa» —
            lo storico dei riavvii viene ruotato e per le corse più vecchie la risposta non
            esiste. Clicca una riga per vedere i pacchetti.
          </p>
        </>
      )}
    </div>
  );
}

function descriviInterruttore(v: boolean | null): string {
  if (v === true) return 'attivi';
  if (v === false) return 'DISATTIVATI';
  return 'non rilevato';
}

function RiavvioDopo({ valore, kernel }: { valore: boolean | null; kernel: boolean }) {
  if (valore === null) return <span className="bop-text-muted">non si sa</span>;
  if (valore) return <span className="bop-badge bop-badge-success">sì</span>;
  // Un riavvio mancato pesa solo se quella corsa ha toccato il kernel: per i
  // pacchetti normali l'aggiornamento è già in servizio senza riavviare.
  return kernel
    ? <span className="bop-badge bop-badge-danger">no, kernel non in servizio</span>
    : <span className="bop-badge bop-badge-neutral">no</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Elementi minori
// ─────────────────────────────────────────────────────────────────────────────

function Dato({ etichetta, valore, tono }: { etichetta: string; valore: string; tono?: 'errore' }) {
  return (
    <div className="storico-sistema__dato">
      <span className="storico-sistema__dato-etichetta">{etichetta}</span>
      <span className={`storico-sistema__dato-valore${tono === 'errore' ? ' storico-sistema__dato-valore--errore' : ''}`}>
        {valore}
      </span>
    </div>
  );
}

function BadgeEsito({ esito, messaggio }: { esito: 'OK' | 'ERRORE'; messaggio: string | null }) {
  if (esito === 'ERRORE') {
    // Il messaggio d'errore sta nel title e non in colonna: sono stringhe
    // lunghe (l'output di psql o di rclone) che sfonderebbero la tabella.
    return (
      <span className="bop-badge bop-badge-danger" title={messaggio ?? undefined}>
        Fallito
      </span>
    );
  }
  return <span className="bop-badge bop-badge-success">OK</span>;
}
